import { useEffect, useState } from 'react'
import MulesoftPng from '~/assets/mulesoft.png'
import { Button } from '~/components/common/Button'
import { InputArea } from '~/components/common/InputArea'
import { Selector } from '~/components/common/Selector'
import { Typography } from '~/components/common/Typography'
import { Modal } from '~/components/modals/CurieModal'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { BetterToast } from '~/utils/toast'

interface IntegrationItemModalProps {
  open: boolean
  onClose: () => void
  refreshIntegrations: () => void
  metadata: unknown
  isEditing: boolean
}

const ANYPOINT_REGION_OPTIONS = [
  { value: 'us', label: 'US' },
  { value: 'eu', label: 'EU' },
  { value: 'ca', label: 'Canada' },
  { value: 'jp', label: 'Japan' }
]

export function AnypointExchangeItemModal({
  open,
  onClose,
  refreshIntegrations,
  metadata,
  isEditing
}: IntegrationItemModalProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const [clientId, setClientId] = useState<string>('')
  const [clientSecret, setClientSecret] = useState<string>('')
  const [region, setRegion] = useState<string>('us')
  const [tested, setTested] = useState<boolean>(false)
  const [isTestingConnection, setIsTestingConnection] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const anypointExchangeMetadata = metadata as {
    client_id: string
    client_secret: string
    region?: string
  } | null

  useEffect(() => {
    if (open && anypointExchangeMetadata) {
      setClientId(anypointExchangeMetadata.client_id || '')
      setClientSecret(anypointExchangeMetadata.client_secret || '')
      setRegion(anypointExchangeMetadata.region || 'us')
      if (anypointExchangeMetadata.client_id && anypointExchangeMetadata.client_secret) {
        setTested(true)
      } else {
        setTested(false)
      }
      setErrorMessage('')
    } else if (open) {
      setClientId('')
      setClientSecret('')
      setRegion('us')
      setTested(false)
      setErrorMessage('')
    }
  }, [open, anypointExchangeMetadata])

  const handleInputChange = (field: 'clientId' | 'clientSecret', value: string) => {
    if (field === 'clientId') {
      setClientId(value)
    } else {
      setClientSecret(value)
    }
    // Reset error and tested state when user types
    if (errorMessage || tested) {
      setErrorMessage('')
      setTested(false)
    }
  }

  const handleRegionChange = (value: string) => {
    setRegion(value)
    if (errorMessage || tested) {
      setErrorMessage('')
      setTested(false)
    }
  }

  const handleTestConnection = async () => {
    const wrongRegionErrorText = 'API keys or region incorrect. Please update and retry.'

    if (!clientId || !clientSecret) {
      setErrorMessage('Please enter both Client ID and Client Secret')
      return
    }

    if (!currentWorkspace?.uuid) {
      setErrorMessage('Workspace not found')
      return
    }

    setIsTestingConnection(true)
    setErrorMessage('')
    try {
      const data = await fetchWrapper.post<{ success: boolean; message: string; region?: string }>(
        `/api/workspace/${currentWorkspace.uuid}/integration/anypoint_exchange/test`,
        {
          client_id: clientId,
          client_secret: clientSecret,
          region
        },
        { credentials: 'include' }
      )

      if (data.success) {
        if (data.region) {
          setRegion(data.region)
        }
        setTested(true)
        setErrorMessage('')
      } else {
        setTested(false)
        setErrorMessage(wrongRegionErrorText)
      }
    } catch (error) {
      setTested(false)
      setErrorMessage(wrongRegionErrorText)
      console.error('Test connection error:', error)
    } finally {
      setIsTestingConnection(false)
    }
  }

  const handleSaveConfiguration = async () => {
    if (!currentWorkspace?.uuid) {
      BetterToast.error('Workspace not found')
      return
    }

    setIsSaving(true)
    try {
      const method = isEditing ? 'put' : 'post'
      const data = await fetchWrapper[method]<{ success: boolean; message: string }>(
        `/api/workspace/${currentWorkspace.uuid}/integration/anypoint_exchange`,
        {
          client_id: clientId,
          client_secret: clientSecret,
          region
        },
        { credentials: 'include' }
      )

      if (data.success) {
        BetterToast.success(
          isEditing ? 'Integration updated successfully!' : 'Integration saved successfully!'
        )
        refreshIntegrations()
        onClose()
      } else {
        BetterToast.error(data.message || `Failed to ${isEditing ? 'update' : 'save'} integration`)
      }
    } catch (error) {
      BetterToast.error(`Failed to ${isEditing ? 'update' : 'save'} integration. Please try again.`)
      console.error('Save integration error:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const disabled = isTestingConnection || !clientId || !clientSecret

  return (
    <Modal className='w-[600px]' isOpen={open} onClose={onClose} hideCloseButton={false}>
      <div className='p-4 flex gap-x-4 items-center border-b border-neutral-border-2'>
        <img
          className='w-8 h-8 select-none pointer-events-none'
          src={MulesoftPng}
          alt='Mulesoft'
          draggable={false}
        />
        <Typography fontFamily='geist' fontSize='titleSm' fontWeight='bold' className='select-none'>
          Anypoint Exchange
        </Typography>
      </div>
      <div className='p-12'>
        <div className='w-[360px] mx-auto flex flex-col gap-y-4'>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Client Id
            </Typography>
            <InputArea
              value={clientId}
              onChange={e => handleInputChange('clientId', e.target.value)}
              fullWidth
              singleLine
            />
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Client Secret
            </Typography>
            <InputArea
              value={clientSecret}
              onChange={e => handleInputChange('clientSecret', e.target.value)}
              fullWidth
              singleLine
            />
          </div>
          <div className='flex flex-col gap-y-2'>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              fontWeight='semibold'
              className='select-none'
            >
              Region
            </Typography>
            <Selector
              aria-label='Anypoint region'
              size='large'
              options={ANYPOINT_REGION_OPTIONS.map(option => ({ key: option.value, ...option }))}
              value={region}
              onSelectionChange={handleRegionChange}
              className='h-10 w-full'
              triggerClassName='h-10 rounded-xl border-[#D4DCE2] px-3 py-0 focus:border-[#846fe1]'
            />
          </div>
          <div className='flex items-center justify-end gap-x-2'>
            {errorMessage && (
              <Typography
                fontFamily='geistMono'
                fontSize='labelSm'
                className='text-tokens-status-error-text'
                fontWeight='semibold'
              >
                {errorMessage}
              </Typography>
            )}
            <Button
              variant='borderless'
              className={(() => {
                if (tested) return 'bg-tokens-neutral-fill-fill-6'
                return undefined
              })()}
              onClick={handleTestConnection}
              isDisabled={disabled}
            >
              <div className='flex gap-x-2 items-center select-none'>
                {isTestingConnection ? (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      className='animate-spin'
                    >
                      <path
                        d='M8 1.5C6.71442 1.5 5.45772 1.88122 4.3888 2.59545C3.31988 3.30968 2.48676 4.32484 1.99479 5.51256C1.50282 6.70028 1.37409 8.00721 1.6249 9.26809C1.8757 10.529 2.49477 11.6872 3.40381 12.5962C4.31285 13.5052 5.47104 14.1243 6.73192 14.3751C7.99279 14.6259 9.29973 14.4972 10.4874 14.0052C11.6752 13.5132 12.6903 12.6801 13.4046 11.6112C14.1188 10.5423 14.5 9.28558 14.5 8'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                      Testing...
                    </Typography>
                  </>
                ) : tested ? (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        fillRule='evenodd'
                        clipRule='evenodd'
                        d='M7.99961 14.4C11.5342 14.4 14.3996 11.5346 14.3996 8.00001C14.3996 4.46538 11.5342 1.60001 7.99961 1.60001C4.46499 1.60001 1.59961 4.46538 1.59961 8.00001C1.59961 11.5346 4.46499 14.4 7.99961 14.4ZM10.9653 6.96569C11.2777 6.65327 11.2777 6.14674 10.9653 5.83432C10.6529 5.5219 10.1463 5.5219 9.83392 5.83432L7.19961 8.46863L6.16529 7.43432C5.85288 7.1219 5.34634 7.1219 5.03392 7.43432C4.7215 7.74674 4.7215 8.25327 5.03392 8.56569L6.63392 10.1657C6.94634 10.4781 7.45288 10.4781 7.76529 10.1657L10.9653 6.96569Z'
                        fill='#2C8450'
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                      Test
                    </Typography>
                  </>
                ) : errorMessage ? (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M7.99967 14.5953C4.31767 14.5953 1.33301 11.6107 1.33301 7.92866C1.33301 4.24733 4.31767 1.26199 7.99967 1.26199C11.6817 1.26199 14.6663 4.24733 14.6663 7.92866C14.6663 11.6107 11.6817 14.5953 7.99967 14.5953ZM7.99967 4.59533C7.82286 4.59533 7.65329 4.66556 7.52827 4.79059C7.40325 4.91561 7.33301 5.08518 7.33301 5.26199V8.59533C7.33301 8.77214 7.40325 8.94171 7.52827 9.06673C7.65329 9.19176 7.82286 9.26199 7.99967 9.26199C8.17649 9.26199 8.34605 9.19176 8.47108 9.06673C8.5961 8.94171 8.66634 8.77214 8.66634 8.59533V5.26199C8.66634 5.08518 8.5961 4.91561 8.47108 4.79059C8.34605 4.66556 8.17649 4.59533 7.99967 4.59533ZM7.99967 11.262C8.17649 11.262 8.34605 11.1918 8.47108 11.0667C8.5961 10.9417 8.66634 10.7721 8.66634 10.5953C8.66634 10.4185 8.5961 10.2489 8.47108 10.1239C8.34605 9.9989 8.17649 9.92866 7.99967 9.92866C7.82286 9.92866 7.65329 9.9989 7.52827 10.1239C7.40325 10.2489 7.33301 10.4185 7.33301 10.5953C7.33301 10.7721 7.40325 10.9417 7.52827 11.0667C7.65329 11.1918 7.82286 11.262 7.99967 11.262V11.262Z'
                        fill='#D94B36'
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                      Test Again
                    </Typography>
                  </>
                ) : (
                  <>
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M14.5 3C14.6326 3 14.7598 3.05268 14.8536 3.14645C14.9473 3.24021 15 3.36739 15 3.5V12.5C15 12.6326 14.9473 12.7598 14.8536 12.8536C14.7598 12.9473 14.6326 13 14.5 13H1.5C1.36739 13 1.24021 12.9473 1.14645 12.8536C1.05268 12.7598 1 12.6326 1 12.5V3.5C1 3.36739 1.05268 3.24021 1.14645 3.14645C1.24021 3.05268 1.36739 3 1.5 3H14.5ZM1.5 2C1.10218 2 0.720644 2.15804 0.43934 2.43934C0.158035 2.72064 0 3.10218 0 3.5L0 12.5C0 12.8978 0.158035 13.2794 0.43934 13.5607C0.720644 13.842 1.10218 14 1.5 14H14.5C14.8978 14 15.2794 13.842 15.5607 13.5607C15.842 13.2794 16 12.8978 16 12.5V3.5C16 3.10218 15.842 2.72064 15.5607 2.43934C15.2794 2.15804 14.8978 2 14.5 2H1.5Z'
                        fill='#262626'
                        fillOpacity={disabled ? '0.2' : '1'}
                      />
                    </svg>
                    <Typography fontFamily='geist' fontSize='bodySm' fontWeight='semibold'>
                      Test Connection
                    </Typography>
                  </>
                )}
              </div>
            </Button>
          </div>
        </div>
      </div>
      <div className='py-4 px-8 flex gap-2 bg-tokens-neutral-bg-grey-1 border-t border-tokens-neutral-border-2 justify-end items-center select-none'>
        <div className='flex gap-2'>
          <Button
            variant='outlined'
            onClick={onClose}
            className='text-[#262626] border-[#D6D6D6] bg-white hover:bg-gray-50'
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            isDisabled={!tested || isSaving}
            onClick={handleSaveConfiguration}
          >
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Add Configuration'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
